'use client';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronUp as ChevronUpIcon } from 'lucide-react';

import type { StoryGroup } from '@nutralab/api';
import { Icon, Text } from '@nutralab/ui';

import { classNames } from '@m1work/shared-utils';

import { StoryViewer, type StoryViewerData } from '../StoryViewer';

import styles from './StoriesRow.module.scss';
import { useViewedStories } from './useViewedStories';

import { useModal } from '@/app/_providers/ModalProvider';
import { getImagePath, getImageUrl } from '@/shared/utils';

const MOBILE_COLUMNS = 4;
const MOBILE_COLLAPSED_COUNT = MOBILE_COLUMNS - 1;

const RING_TOTAL_LENGTH = 100;
const RING_RADIUS = 47;
const RING_STROKE_WIDTH = 3;
const RING_MAX_GAP = 3;
const RING_GAP_BUDGET = 40;
const RING_ROUND_CAP_MIN_GAP = 2;

interface StoryRingProps {
    viewed: boolean[];
}

function StoryRing({ viewed }: StoryRingProps): React.ReactElement {
    const count = viewed.length;
    const segment = RING_TOTAL_LENGTH / count;
    const gap = count > 1 ? Math.min(RING_MAX_GAP, RING_GAP_BUDGET / count) : 0;
    const arc = segment - gap;

    return (
        <svg className={styles.ring} viewBox="0 0 100 100" aria-hidden="true">
            {viewed.map((isViewed, index) => (
                <circle
                    key={index}
                    cx="50"
                    cy="50"
                    r={RING_RADIUS}
                    fill="none"
                    stroke={isViewed ? 'var(--color-highlight)' : 'var(--color-accent)'}
                    strokeWidth={RING_STROKE_WIDTH}
                    strokeLinecap={gap >= RING_ROUND_CAP_MIN_GAP ? 'round' : 'butt'}
                    pathLength={RING_TOTAL_LENGTH}
                    strokeDasharray={`${arc} ${RING_TOTAL_LENGTH - arc}`}
                    strokeDashoffset={-(index * segment)}
                />
            ))}
        </svg>
    );
}

interface StoriesRowProps extends React.HTMLAttributes<HTMLDivElement> {
    groups: StoryGroup[];
}

/** The row of story avatars: opens the story viewer and marks the groups already seen. */
export function StoriesRow({ groups, className, ...props }: StoriesRowProps): React.ReactElement | null {
    const { t } = useTranslation();
    const { openModal } = useModal();
    const [isExpanded, setIsExpanded] = useState(false);

    const visibleGroups = useMemo(() => groups.filter((g) => g.media.length > 0), [groups]);
    const { viewed, markViewed } = useViewedStories(visibleGroups);

    if (visibleGroups.length === 0) {
        return null;
    }

    const hasToggle = visibleGroups.length > MOBILE_COLUMNS;
    const hiddenCount = visibleGroups.length - MOBILE_COLLAPSED_COUNT;

    const handleOpen = (index: number): void => {
        const firstUnviewed = visibleGroups[index]?.media.findIndex((m) => !viewed.has(getImagePath(m.url))) ?? 0;

        openModal<StoryViewerData>({
            ModalComponent: StoryViewer,
            data: {
                groups: visibleGroups,
                initialIndex: index,
                initialSlideIndex: Math.max(firstUnviewed, 0),
                onSlideViewed: markViewed,
            },
        });
    };

    return (
        <div className={classNames(styles.row, hasToggle && !isExpanded && styles.collapsed, className)} {...props}>
            {visibleGroups.map(({ name, avatar, media }, index) => (
                <button key={index} type="button" className={styles.item} onClick={() => handleOpen(index)}>
                    <span className={styles.bubble}>
                        <img src={getImageUrl(avatar)} alt={name} className={styles.avatar} loading="lazy" />
                        <StoryRing viewed={media.map((m) => viewed.has(getImagePath(m.url)))} />
                    </span>
                    <Text as="span" weight="semibold" className={styles.label}>
                        {name}
                    </Text>
                </button>
            ))}

            {hasToggle && (
                <button
                    type="button"
                    className={styles.toggle}
                    aria-expanded={isExpanded}
                    aria-label={t(isExpanded ? 'landing.main-product-list.hide' : 'landing.main-product-list.show-all')}
                    onClick={() => setIsExpanded((v) => !v)}
                >
                    <span className={classNames(styles.bubble, styles['toggle-bubble'])}>
                        {isExpanded ? (
                            <Icon icon={ChevronUpIcon} size="3xl" />
                        ) : (
                            <span className={styles['toggle-count']}>{`+${hiddenCount}`}</span>
                        )}
                    </span>
                </button>
            )}
        </div>
    );
}
